import React, { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { c } from 'ttag';

import {
    Alert,
    AuthStep,
    AuthenticationStore,
    Button,
    Divider,
    Modal,
    useAuthentication,
    useLocalState,
} from '@freelancelabs/components';
import { KeyOutline } from '@freelancelabs/icons';

import PublicLayout from '../app/content/PublicLayout';
import EmailOtpForm from '../app/login/EmailOtpForm';
import LoginForm from '../app/login/LoginForm';
import SetPasswordForm from '../app/login/SetPasswordForm';
import TotpCodeForm from '../app/login/TotpCodeForm';
import { AlertType, alertMessages, defaultUsernameKey } from '../constants';

const LoginPage = () => {
    const location = useLocation();
    const { completeNewPassword, confirmMfaCode, login } = useAuthentication() as AuthenticationStore;
    const [username, setUsername] = useLocalState('', defaultUsernameKey);
    const [step, setStep] = useState(AuthStep.LOGIN);
    // mot de passe garde en memoire le temps de l'etape MFA, pour pouvoir renvoyer un code
    const [mfaPassword, setMfaPassword] = useState('');
    const [mfaChallenge, setMfaChallenge] = useState<'EMAIL_OTP' | 'TOTP_CODE'>('EMAIL_OTP');
    const [searchParams] = useSearchParams();

    /**
     * L'ALERTE VIENT DU ROUTEUR **OU** DE L'URL, et le second chemin existe pour les
     * autres applications.
     *
     * `location.state` ne traverse pas une frontiere d'application : le portail qui
     * deconnecte quelqu'un sans rattachement (PCG-305) atterrit ici par une redirection
     * de navigateur, donc sans etat de routeur. Il passe `?alerte=APP_UNAUTHORIZED`, et
     * c'est le MEME message qui s'affiche : reecrire la phrase la-bas en donnerait deux
     * versions du meme refus, libres de deriver.
     *
     * **La valeur est VERIFIEE contre l'enumeration**, et ce n'est pas de la prudence
     * decorative : `alertMessages()[alert]` leve sur une cle inconnue, donc une adresse
     * bricolee a la main casserait l'ecran de connexion, c'est-a-dire la porte d'entree
     * des trois applications.
     */
    const depuisUrl = searchParams.get('alerte');
    const alert: AlertType =
        location.state?.withAlert ?? (depuisUrl && depuisUrl in AlertType ? (depuisUrl as AlertType) : undefined);

    return (
        <PublicLayout>
            {alert && (
                <Alert
                    type="danger"
                    title={alertMessages()[alert].title}
                    message={alertMessages()[alert].message}
                    className="mb-4"
                />
            )}
            {/* LE FORMULAIRE RESTE MONTE PENDANT LE DEFI MFA, et c'est ce que veut dire
                « modale » : elle se pose PAR-DESSUS quelque chose. Demonte, il laissait la
                colonne de gauche vide derriere la boite, ce qui se lit comme une page qui a
                fini de charger sans rien afficher.

                Deux consequences en plus du dessin. « Annuler » ramene sur un formulaire
                qui porte encore l'adresse saisie, au lieu d'un ecran remis a zero. Et
                l'etape NEW_PASSWORD, elle, n'est PAS dans cette condition : celle-la
                remplace vraiment l'ecran au lieu de le recouvrir. */}
            {(step === AuthStep.LOGIN || step === AuthStep.SSO_EMAIL || step === AuthStep.MFA_CODE) && (
                <>
                    {/*
                        LA BASCULE (PCG-304). Elle ne navigue nulle part et ne remplace pas
                        l'ecran : elle change l'etat du MEME formulaire, comme le fait
                        Cloudflare. Le mot de passe et son lien s'en vont, le bouton d'envoi
                        change de nom, et le champ e-mail garde sa valeur parce que c'est
                        litteralement le meme champ.

                        Attention, LE LIBELLE « Continuer avec le SSO » EST PORTE PAR DEUX
                        CONTROLES A DEUX MOMENTS : ici pour entrer dans le mode, et sur le
                        bouton d'envoi une fois qu'on y est. C'est le dessin de Cloudflare, et
                        c'est ce qui evite un troisieme mot pour une seule idee. Les deux ne
                        sont jamais visibles ensemble sous ce libelle.
                    */}
                    <Button
                        size="large"
                        category="weak"
                        onClick={() => setStep(step === AuthStep.SSO_EMAIL ? AuthStep.LOGIN : AuthStep.SSO_EMAIL)}
                    >
                        <KeyOutline />
                        {step === AuthStep.SSO_EMAIL
                            ? c('Sso').t`Continue with password`
                            : c('Sso').t`Continue with SSO`}
                    </Button>
                    {/*
                        LE BOUTON « Me connecter sans mot de passe » EST RETIRE DE L'ECRAN,
                        decide par Mohamed le 2026-08-28, et c'est le meme geste que la barre
                        d'onglets de « Mon activite » a PCG-129 : ON RETIRE LA PORTE, PAS LA
                        PIECE. La route `/login/password-less`, sa page et
                        `passwordLessLogin` restent tous en place ; le lien recu par courriel
                        continue donc de fonctionner, et il n'y a rien a redeployer pour
                        remettre le bouton.

                        Consequence assumee : la page n'est plus atteignable que par son URL.
                    */}
                    <Divider className="my-8">{c('Divider').t`or`}</Divider>
                    <LoginForm
                        defaultUsername={username}
                        ssoMode={step === AuthStep.SSO_EMAIL}
                        onUsernameChange={setUsername}
                        onSubmit={(data) => {
                            setUsername(data.payload.username);
                            if (data.challenge === 'NEW_PASSWORD_REQUIRED') {
                                setStep(AuthStep.NEW_PASSWORD);
                            }
                            if (data.challenge === 'EMAIL_OTP' || data.challenge === 'TOTP_CODE') {
                                setMfaChallenge(data.challenge);
                                setMfaPassword(data.payload.password);
                                setStep(AuthStep.MFA_CODE);
                            }
                        }}
                    />
                </>
            )}
            <Modal
                open={step === AuthStep.MFA_CODE}
                onClose={() => setStep(AuthStep.LOGIN)}
                title={c('Mfa title').t`Security verification`}
                size={560}
                panelProps={{ className: 'modal_panel-container mfa-modal' }}
            >
                {mfaChallenge === 'EMAIL_OTP' ? (
                    <EmailOtpForm
                        email={username}
                        onSubmit={(code) => confirmMfaCode(code)}
                        onResend={async () => {
                            await login({ email: username, password: mfaPassword });
                        }}
                        onCancel={() => setStep(AuthStep.LOGIN)}
                    />
                ) : (
                    <TotpCodeForm onSubmit={(code) => confirmMfaCode(code)} onCancel={() => setStep(AuthStep.LOGIN)} />
                )}
            </Modal>
            {step === AuthStep.NEW_PASSWORD && (
                <>
                    <h1 className="text-2xl font-semibold text-text-norm pb-2">
                        {c('New password required title').t`Create a new password`}
                    </h1>
                    <span className="text-text-norm pb-6">
                        {c('New password required help').t`For security reasons you must set a new password.`}
                    </span>
                    <SetPasswordForm
                        username={username}
                        onSubmit={(newPassword) => {
                            return completeNewPassword(newPassword);
                        }}
                    />
                </>
            )}
        </PublicLayout>
    );
};
export default LoginPage;
